import { Box, Stack, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';

import { notEmpty } from 'utils/typescript.utils';

import { useAccount } from 'queries/accounts/useAccount';
import { useTicketInviteView } from 'services/datalayer/tickets';
import { PageEventFragment } from 'types/api/dato-operations.types';
import { TransferFragment } from 'types/api/tickets.types';

import FormattedDateTime from 'components/@events/FormattedDateTime';

import TransferTicket from './TransferTicket';

interface Props {
  event: PageEventFragment;
  hasMultipleEvents: boolean;
  transfer: TransferFragment;
}

const TransferDetails = ({ event, hasMultipleEvents, transfer }: Props) => {
  const intl = useIntl();
  useTicketInviteView({ inviteSource: 'direct' });
  const { account } = useAccount();
  const receiverName = account?.firstName || transfer.receiverName;
  const senderName = transfer?.senderName;

  const title = [
    intl.formatMessage({ id: 'transfer_tickets.receiver.title' }),
    receiverName,
  ]
    .filter(notEmpty)
    .join(' ');

  return (
    <Box
      sx={{
        flex: '1',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack
        sx={{
          gap: 1,
          mb: 3,
        }}
      >
        <Typography variant="h6" component="h1">
          {title},
        </Typography>
        <Typography variant="body1">
          {!!senderName ? (
            <FormattedMessage
              id="transfer_tickets.sender.description"
              values={{
                name: senderName,
                tickets: transfer?.tickets.length,
                strong: (chunks) => <strong>{...chunks}</strong>,
              }}
            />
          ) : (
            <FormattedMessage
              id="transfer_tickets.sender.anonymous"
              values={{
                name: senderName,
                tickets: transfer?.tickets.length,
              }}
            />
          )}
        </Typography>
      </Stack>

      <Stack
        sx={{
          gap: 0.5,
          mb: 2,
        }}
      >
        {!hasMultipleEvents && (
          <>
            <Typography variant="h7" component="h2">
              {event.title}
            </Typography>

            <FormattedDateTime
              endDatetime={event.endDatetime}
              startDatetime={event.startDatetime}
              timezone={event.timezone}
            />
          </>
        )}
      </Stack>
      <Stack
        sx={{
          gap: 1,
          flex: '1',
          mb: 5,
        }}
      >
        {transfer?.tickets.map((ticket) => (
          <TransferTicket key={ticket.id} ticket={ticket} />
        ))}
      </Stack>
    </Box>
  );
};

export default TransferDetails;
